'use client'

import "./globals.css"
import AuthProvider from "@/components/AuthProvider"
import Toast from "@/components/Toast";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "sonner";
import { Geist, Geist_Mono, Inter, Source_Serif_4, JetBrains_Mono } from 'next/font/google';
import '@/styles/index.css';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { PREFS_BOOT } from '@/components/g/prefs';
import { GlobalAlertDialog } from '@/components/GlobalAlertDialog';
import '@/utils/markdownPolyfill'; // Apply isSpace polyfill globally
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { initGA, trackPageView } from '@/lib/analytics';

// Signed-in app fonts (src/styles/tokens.css). Own variable names: the landing below already uses --font-geist for Inter.
const geist = Geist({ subsets: ['latin'], weight: ['400', '500', '600'], variable: '--font-app', display: 'swap' });
const geistMono = Geist_Mono({ subsets: ['latin'], weight: ['400'], variable: '--font-app-mono', display: 'swap' });

// Configure Inter font
const inter = Inter({
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-inter',
});

// Glacien design fonts (used by the v2 landing — now mounted at root)
const v2Sans = Inter({
  subsets: ['latin'],
  weight: ['400', '500', '600'],
  variable: '--font-geist',
  display: 'swap',
});

const v2Serif = Source_Serif_4({
  subsets: ['latin'],
  weight: ['400', '500'],
  style: ['normal', 'italic'],
  variable: '--font-fraunces',
  display: 'swap',
});

const v2Mono = JetBrains_Mono({
  subsets: ['latin'],
  weight: ['400', '500'],
  variable: '--font-jetbrains',
  display: 'swap',
});

export default function RootLayout({ children }) {
  const pathname = usePathname();

  // Initialize Google Analytics 4
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID) {
      initGA();
    }
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (pathname) {
      trackPageView(pathname);
    }
  }, [pathname]);

  return (
    <html lang="en" className={`${geist.variable} ${geistMono.variable} ${inter.variable} ${v2Sans.variable} ${v2Serif.variable} ${v2Mono.variable}`} data-scroll-behavior="smooth" suppressHydrationWarning>
      <head>
        {/* Applies the saved theme, accent and density before the first paint (see prefs.js). */}
        <script dangerouslySetInnerHTML={{ __html: PREFS_BOOT }} />
        {/* Smart App Banner for iOS */}
        <meta name="apple-itunes-app" content="app-id=6738255643" />

        {/* Android/Chrome App Banner meta tags */}
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="theme-color" content="#2764FE" />

        {/* Deep linking meta tags for app installation */}
        <meta name="google-play-app" content="app-id=com.agentava.aiva" />

        {/* Open Graph for social sharing */}
        <meta property="og:title" content="AVA - Your Voice-Powered Productivity Super App" />
        <meta property="og:description" content="Plan your day in 10 seconds — just talk to AVA. Voice-first AI that replaces 10+ apps." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.agentava.ai" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AVA - Your Voice-Powered Productivity Super App" />
        <meta name="twitter:description" content="Plan your day in 10 seconds — just talk to AVA." />
      </head>
      <body className="antialiased">
        {/* Google Analytics gtag.js */}
        <Script
          strategy="afterInteractive"
          src={`https://www.googletagmanager.com/gtag/js?id=G-KPX30CFEFB`}
        />
        <Script
          id="google-analytics"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-KPX30CFEFB');
            `,
          }}
        />

        <ThemeProvider>
          <AuthProvider>
            {children}
            <Toast />
            <Toaster />
            <Sonner
              position="top-center"
              toastOptions={{
                unstyled: true,
                classNames: {
                  toast: 'w-screen fixed left-0 right-0',
                  title: 'text-white',
                  description: 'text-white',
                }
              }}
            />
            <GlobalAlertDialog />
          </AuthProvider>
        </ThemeProvider>
      </body>
    </html>
  )
}
